<template>
    <tailwind-layout title="Maintenance Requests">

        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden md:flex justify-between items-center"><!-- Block for lg and larger -->
                <h1 class="font-semibold text-2xl leading-tight">Maintenance Requests - {{ property.full_address }}</h1>
                <div class="flex items-center space-x-4">
                    <span v-if="$page.props.user.role === 'applicant'" class="isolate inline-flex rounded-md shadow-sm">
                        <inertia-link :href="route('my-property', { tab: 'maintenance' })"
                        class="whiteButton">
                            <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                        </inertia-link>
                        <inertia-link :href="route('maintenance-requests.create', property.id)"
                                      class="primaryButton ml-2">
                            <span class="">New Request</span>
                        </inertia-link>
                    </span>
                    <span v-else class="isolate inline-flex rounded-md shadow-sm">
                        <inertia-link :href="route('properties.show', property.id) + '?tab=maintenance'"
                        class="whiteButton">
                            <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                        </inertia-link>
                    </span>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-start md:items-center">
                    <div class="flex flex-col">
                        <h1 class="font-semibold text-2xl leading-tight">Maintenance Requests - {{ property.full_address }}</h1>
                        <span v-if="$page.props.user.role === 'applicant'" class="isolate inline-flex my-4">
                            <inertia-link :href="route('my-property', { tab: 'maintenance' })"
                                class="whiteButton">
                                    <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                                </inertia-link>
                                <inertia-link :href="route('maintenance-requests.create', property.id)"
                                            class="primaryButton ml-2">
                                    <span class="">New Request</span>
                                </inertia-link>
                        </span>
                        <span v-else class="isolate inline-flex my-4">
                            <inertia-link :href="route('properties.show', property.id) + '?tab=maintenance'"
                            class="whiteButton">
                                <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                            </inertia-link>
                        </span>
                    </div>
                </div>
            </div>
        </template>
        <alert-card v-if="$page.props.user.role === 'applicant'" type="info" class="my-3">
            <span>
                If you are experiencing any issues with this property, please submit a maintenance request by providing a clear description of the problem. Be as specific as possible to help the maintenance team resolve the issue quickly. After submitting, you will receive updates regarding the status of your request.
            </span>
        </alert-card>

        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-3 sm:grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch sm:ml-2 lg:ml-0">
                    <div class="w-full sm:w-2/12 pr-4 order-2 sm:order-1 px-2">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of requests per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="col-span-3 sm:w-6/12 sm:pr-4 order-1 sm:order-2 px-2">
                        <input id="search" v-model="form.search" type="text" placeholder="Search for maintenance requests..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md sm:mx-2">
                        <div class="flex items-center mt-2 md:ml-2" @click="show_archived_requests()">
                            <SwitchGroup as="div" class="flex items-center" v-model="form.trashed">
                                <Switch :class="[form.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']">
                                    <span aria-hidden="true" :class="[form.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                </Switch>
                                <SwitchLabel as="span" class="ml-3 text-sm">
                                    <span class="font-medium text-gray-700">Show Archived Requests</span>
                                </SwitchLabel>
                            </SwitchGroup>
                        </div>
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left cursor-pointer">
                                            <span @click="sortTable('emergency_types_id')">
                                                Request Type
                                                <icon v-show="form.sort_by === 'emergency_types_id'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'emergency_types_id'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" width="30%" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Description
                                        </th>
                                        <th v-if="$page.props.user.role === 'agent'" scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Receipts
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Attachments
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left cursor-pointer">
                                            <span @click="sortTable('created_at')">
                                                Created at
                                                <icon v-show="form.sort_by === 'created_at'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'created_at'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Status
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">

                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200">
                                    <tr v-for="(maintenanceRequest, index) in maintenanceRequests.data" :key="index"
                                           :class="[(index % 2 === 0 && !maintenanceRequest.deleted_at ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : (!maintenanceRequest.deleted_at) ? 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer' : ''),
                                        (maintenanceRequest.deleted_at ? 'bg-red-100 !border-b !border-red-200 hover:bg-red-200 hover:cursor-pointer' : '')]"
                                        @click.stop="show_property_page(maintenanceRequest)"
                                    >
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ maintenanceRequest.emergency_type_name ?? 'Other' }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ maintenanceRequest.other_description }}
                                        </td>
                                        <td v-if="$page.props.user.role === 'agent'" class="whitespace-nowrap py-4 pl-3 pr-3 text-sm font-medium text-gray-900">
                                            <button
                                                v-if="!maintenanceRequest.deleted_at"
                                                @click.stop="showReceipts(maintenanceRequest)"
                                                class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                            >
                                                View
                                            </button>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <div v-if="maintenanceRequest.images && maintenanceRequest.images.length > 0" class="flex space-x-4">
                                              <button @click="viewAllAttachments(maintenanceRequest)" class="font-medium text-indigo-600 hover:text-indigo-500" @click.native.stop>View All</button>
                                              <button @click="downloadAllAttachments(maintenanceRequest)" class="font-medium text-indigo-600 hover:text-indigo-500" @click.native.stop>Download All</button>
                                            </div>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ maintenanceRequest.created_at }}
                                        </td>
                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                            <span v-if="!maintenanceRequest.deleted_at" :class="getStatusColor(maintenanceRequest.status)">
                                                {{ maintenanceRequest.status }}
                                            </span>
                                            <span v-if="maintenanceRequest.deleted_at" class="text-red-700 bg-red-50 ring-red-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset">
                                                Archived
                                            </span>
                                        </td>
                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                            <inertia-link :href="route('maintenance-requests.show', [property.id, maintenanceRequest.id])"
                                                class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                View
                                            </inertia-link>
                                            <button v-if="maintenanceRequest.deleted_at && $page.props.user.role === 'agent'"
                                                title="Restore Request"
                                                @click="restoreRequest(maintenanceRequest)"
                                                class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                Restore
                                            </button>

                                        </td>
                                    </tr>
                                    <tr v-show="!maintenanceRequests.data.length">
                                        <td colspan="5" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No maintenance requests were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(maintenanceRequest, index) in maintenanceRequests.data" :key="index" :class="[maintenanceRequest.deleted_at ? 'bg-red-100 hover:bg-red-50 hover:outline-red-500 divide-red-300' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border']">
                                        <inertia-link :href="route('maintenance-requests.show', [maintenanceRequest.property_id, maintenanceRequest.id])">
                                        <div class="flex flex-col p-6">
                                            <h3 class="truncate text-sm font-medium text-gray-900">
                                                {{ maintenanceRequest.emergency_type_name ?? 'Other' }}
                                                <span
                                                v-if="maintenanceRequest.deleted_at"
                                                class="inline-block text-red-800 bg-red-100 border border-red-300 rounded-full px-3 py-0.5 text-xs font-semibold"
                                                >
                                                Archived
                                                </span>
                                            </h3>
                                            <h3 v-if="maintenanceRequest.other_description" class="truncate text-sm font-medium text-gray-900">{{ maintenanceRequest.other_description }}</h3>
                                            <h3 class="truncate text-sm font-medium text-gray-900 flex gap-2">Attachments:
                                                <div>
                                                    <ol class="list-inside text-sm">
                                                        <li v-for="(image, key) in maintenanceRequest.images" :key="key">
                                                            <a :href="route('maintenance-requests.view-attachment', [maintenanceRequest.property_id, image.id])" target="_blank" class="font-medium text-indigo-600 hover:text-indigo-500" @click.native.stop>View</a>
                                                            <a :href="route('maintenance-requests.download-attachment', [maintenanceRequest.property_id, image.id])" target="_blank" class="font-medium text-indigo-600 hover:text-indigo-500 ml-4" @click.native.stop>Download</a>
                                                        </li>
                                                    </ol>
                                                </div>
                                            </h3>
                                            <h3 class="truncate text-sm font-medium text-gray-900">Created At: {{ maintenanceRequest.created_at }}</h3>
                                            <h3 class="truncate text-sm font-medium text-gray-900">
                                                <span v-if="!maintenanceRequest.deleted_at">
                                                    Status: {{ maintenanceRequest.status }}
                                                </span>

                                            </h3>
                                        </div>
                                        </inertia-link>
                                        <div>
                                            <div :class="[maintenanceRequest.deleted_at ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                                <div class="flex w-0 flex-1">
                                                    <inertia-link :href="route('maintenance-requests.show', [maintenanceRequest.property_id, maintenanceRequest.id])" :class="[maintenanceRequest.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-eyes" />
                                                        View
                                                    </inertia-link>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="maintenanceRequests.links" :results="maintenanceRequests" :type="'Maintenance Requests'" />
        </div>
        <div v-if="showRestoreModal">
            <TransitionRoot as="template" :show="showRestoreModal">
                <Dialog as="div" class="relative z-50" @close="showRestoreModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this request?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="restore()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <!-- Receipts -->
        <modal-jetstream :show="showReceiptsModal" :max-width="'4xl'">
            <div class="overflow-x-auto m-4">

                <!-- Header -->
                <div class="max-w-3xl mx-auto mt-8 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                    <h2 class="text-xl font-bold">Add New Receipt</h2>
                </div>

                <!-- Form -->
                    <form @submit.prevent="store" class="space-y-4">
                        <div class="space-y-2 divide-y divide-gray-200">
                            <div class="mt-6 grid grid-cols-3 gap-y-4 gap-x-4 sm:grid-cols-6">

                                <!-- Receipt -->
                                <div class="sm:col-span-2">
                                    <label for="receipt" :class="receiptForm.errors.receipt ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Receipt <span class="text-red-600">*</span>
                                    </label>
                                    <div class="mt-1">
                                        <input
                                            ref="receiptFile"
                                            :key="receiptInputKey"
                                            type="file"
                                            @input="receiptForm.receipt = $event.target.files[0]"
                                            id="receipt"
                                            :class="receiptForm.errors.receipt ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md"
                                        >
                                        <p v-if="receiptForm.errors.receipt" class="text-red-500 text-sm">{{ receiptForm.errors.receipt }}</p>
                                    </div>
                                </div>

                                <!-- Price -->
                                <div class="sm:col-span-2">
                                    <label for="price" :class="receiptForm.errors.price ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Price <span class="text-red-600">*</span>
                                    </label>
                                    <div class="mt-1">
                                        <input type="number" v-model="receiptForm.price" id="price" step="0.01"
                                            :class="receiptForm.errors.price ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                                        <p v-if="receiptForm.errors.price" class="text-red-500 text-sm">{{ receiptForm.errors.price }}</p>
                                    </div>
                                </div>

                                <!-- Vendor Name -->
                                <div class="sm:col-span-2">
                                    <label for="vendor_name" :class="receiptForm.errors.vendor_name ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Vendor Name <span class="text-red-600">*</span>
                                    </label>
                                    <div class="mt-1">
                                        <input type="text" v-model="receiptForm.vendor_name" id="vendor_name"
                                            :class="receiptForm.errors.vendor_name ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                                        <p v-if="receiptForm.errors.vendor_name" class="text-red-500 text-sm">{{ receiptForm.errors.vendor_name }}</p>
                                    </div>
                                </div>
                                <div class="sm:col-span-2">
                                    <label for="date" :class="receiptForm.errors.date ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Date <span class="text-red-600">*</span>
                                    </label>
                                    <div class="mt-1">
                                        <input type="date" v-model="receiptForm.date" id="date"
                                            :class="receiptForm.errors.date ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                                        <p v-if="receiptForm.errors.date" class="text-red-500 text-sm">{{ receiptForm.errors.vendor_name }}</p>
                                    </div>
                                </div>

                                <!-- Notes -->
                                <div class="sm:col-span-4">
                                    <label for="notes" :class="receiptForm.errors.notes ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Notes
                                    </label>
                                    <div class="mt-1">
                                        <textarea id="notes" v-model="receiptForm.notes" rows="2"
                                            :class="receiptForm.errors.notes ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block sm:text-sm border p-2 rounded-md"></textarea>
                                        <p v-if="receiptForm.errors.notes" class="text-red-500 text-sm">{{ receiptForm.errors.notes }}</p>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <!-- Submit Button -->
                        <div class="text-right pr-2 flex justify-end">
                            <button @click="showReceiptsModal = false"
                                class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 mr-2">
                                Cancel
                            </button>
                            <form-button :form="receiptForm" type="submit">
                                Save
                            </form-button>
                        </div>
                    </form>
                <div>
                    <!-- Previous Receipts Header -->
                    <div class="max-w-3xl mx-auto mt-4 mb-4 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                        <h2 class="text-xl font-bold">Previous Receipts</h2>
                        <a v-if="selectedRequest.receipts.length" target="_blank" :href="route('maintenance-requests.download-all-receipts', selectedRequest.id)" class="text-blue-600 underline text-sm">Download All Receipts</a>
                    </div>

                    <!-- Receipts Table -->
                    <div class="flex flex-col">
                        <div class="overflow-x-auto">
                            <div class="align-middle inline-block min-w-full">
                                <div class="shadow overflow-hidden lg:border">
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead class="bg-blue-50">
                                            <tr>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Date Uploaded
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Price
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Vendor Name
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Notes
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(receipt, index) in selectedRequest.receipts" :key="index"
                                                class="bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer">
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    {{ receipt.formatted_date }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    ${{ receipt.formatted_price }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    {{ receipt.vendor_name }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    {{ receipt.notes }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900 flex space-x-4">
                                                    <a target="_blank" :href="route('maintenance-requests.view-receipt', receipt.id)"
                                                        class="font-medium text-indigo-600 hover:text-indigo-500">View</a>
                                                    <a target="_blank" :href="route('maintenance-requests.download-receipt', receipt.id)"
                                                        class="font-medium text-indigo-600 hover:text-indigo-500">Download</a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

    <!-- Close Button -->
    <button @click="showReceiptsModal = false"
        class="bg-white py-2 px-4 m-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">
        Close
    </button>

</modal-jetstream>

    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import {Switch, SwitchLabel, SwitchGroup} from "@headlessui/vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronLeft, faPlus, faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
import AlertCard from "@/Shared/AlertCard.vue";
import ModalJetstream from "@/Jetstream/Modal.vue";
import FormButton from '@/Shared/FormButton.vue';
import { router } from '@inertiajs/vue3'

export default {
    components: {
        SwitchGroup,
        SwitchLabel,
        Switch,
        Button,
        Icon,
        Pagination,
        TailwindLayout,
        FontAwesomeIcon,
        AlertCard,
        ModalJetstream,
        FormButton
    },
    props: {
        filters: Object,
        property: Object,
        maintenanceRequests: Object,
    },
    data() {
        return {
            form: {
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate)?this.filters.paginate:25,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            showRestoreModal: false,
            selectedRequest: null,
            showReceiptsModal: false,

            receiptForm: this.$inertia.form({
                receipt: null,
                price: null,
                vendor_name: null,
                date: null,
                notes: null,
            }),
            faChevronLeft,
            faPlus,
            faArrowsRotate,
            // Used to force the file input to re-render when resetting
            receiptInputKey: 0,
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('maintenance-requests.index', {maintenance_property: this.property.id}), pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        show_property_page(maintenanceRequest) {
            if(maintenanceRequest.deleted_at === null) {
                this.$inertia.get(this.route('maintenance-requests.show', {maintenance_property: this.property.id, maintenance_request: maintenanceRequest.id}));
            }
         },
        show_archived_requests() {
            this.form.trashed = !this.form.trashed;
        },
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },

        restoreRequest(maintenanceRequest) {
            this.selectedRequest = maintenanceRequest;
            this.showRestoreModal = true;
        },
        restore() {
            this.$inertia.put(this.route('maintenance-requests.restore', {maintenance_property: this.selectedRequest.property_id, maintenance_request: this.selectedRequest.id}), {}, {
                onFinish: () => {
                    this.showRestoreModal = false;
                }
            })
        },
        viewAllAttachments(maintenanceRequest) {
          maintenanceRequest.images.forEach((image, index) => {
            setTimeout(() => {
              window.open(
                this.route('maintenance-requests.view-attachment', [maintenanceRequest.property_id, image.id]),
                '_blank'
              );
            }, index * 300); // Add small delay between each window
          });
        },
        downloadAllAttachments(maintenanceRequest) {
          maintenanceRequest.images.forEach((image, index) => {
            setTimeout(() => {
              // Create a hidden anchor element
              const link = document.createElement('a');
              link.href = this.route('maintenance-requests.download-attachment', [maintenanceRequest.property_id, image.id]);
              link.target = '_blank';
              link.download = '';

              // Append to body and trigger click
              document.body.appendChild(link);
              link.click();

              // Clean up
              document.body.removeChild(link);
            }, index * 300); // Add small delay between each download
          });
        },

        showReceipts(maintenanceRequest) {
            this.selectedRequest = maintenanceRequest;
            this.showReceiptsModal = true;
        },
        store() {
            var selectedRequestId = this.selectedRequest.id;
            this.receiptForm.post(this.route('maintenance-requests.store-receipts', this.selectedRequest.id), {
                preserveState: true,
                onSuccess: () => {
                    const requestsArray = Object.values(this.maintenanceRequests.data);
                    this.selectedRequest = requestsArray.find(request => request.id === selectedRequestId) || null;

                    // Reset form state
                    this.receiptForm.reset();
                    // Clear the actual file input and force re-render to fully reset it
                    if (this.$refs.receiptFile) {
                        this.$refs.receiptFile.value = null;
                    }
                    this.receiptInputKey += 1;
                },
                onError: (errors) => {
                    this.receiptForm.errors = errors;
                }
            });
        },
        getStatusColor(status) {
            const colors = {
                'Submitted': 'text-yellow-700 bg-yellow-50 ring-yellow-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset',
                'In progress': 'text-blue-700 bg-blue-50 ring-blue-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset',
                'Closed': 'text-green-700 bg-green-50 ring-green-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset',
            };
            return colors[status] || 'text-green-700 bg-green-50 ring-green-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset';
        },

        downloadAllReceipts(receipts) {
          receipts.forEach((receipt, index) => {
            setTimeout(() => {
              // Create a hidden anchor element
              const link = document.createElement('a');
              link.href = this.route('maintenance-requests.download-receipt', receipt.id);
              link.target = '_blank';
              link.download = '';

              // Append to body and trigger click
              document.body.appendChild(link);
              link.click();

              // Clean up
              document.body.removeChild(link);
            }, index * 300); // Add small delay between each download
          });
        },
    },
}
</script>
